import Dialog from "@mui/material/Dialog";
import { useQuery, useMutation } from "@apollo/client";
import { Form, ErrorMessage } from "@/shared/components";
import { InnerCrudFormHeader } from "./InnerCrudFormHeader";
import LinearProgress from "@mui/material/LinearProgress";

export interface InnerCrudEditDialogProps<Row = any> {
  open: boolean;
  onClose: () => void;
  id?: string;
  editSchema: any;
  editQuery: any;
  updateMutation: any;
  onSubmitSuccess: (data: any) => void;
  texts: {
    editItem: string | ((data: any) => string);
  };
  refetchQueries?: string[];
  getEditActions?: (args: { id: string }) => React.ReactNode;
  getEditData?: (obj: any) => Row;
  metadata?: Record<string, any>;
  paper?: boolean;
}

export function InnerCrudEditDialog<Row = any>({
  open,
  id,
  onClose,
  editSchema,
  editQuery,
  updateMutation,
  onSubmitSuccess,
  texts,
  refetchQueries = [],
  getEditActions,
  getEditData = (obj: any) => {
    if (!obj) return null;
    if (!obj.edit) return null;
    return JSON.parse(JSON.stringify(obj.edit), (key, value) => {
      return key === "__typename" ? undefined : value;
    });
  },
  metadata,
  paper = true,
}: InnerCrudEditDialogProps<Row>) {
  const editQueryResult = useQuery(editQuery, {
    fetchPolicy: "network-only",
    variables: { id },
    skip: !id,
  });

  const editQueryName = editQuery.definitions.find(
    (definition: any) => definition.operation === "query"
  ).name.value;

  const [updateItem, { loading }] = useMutation(updateMutation, {
    refetchQueries: refetchQueries.concat(editQueryName),
  });

  if (editQueryResult.error) {
    return <ErrorMessage error={editQueryResult.error} />;
  }

  if (!editQueryResult) return <LinearProgress />;
  if (editQueryResult.loading) return <LinearProgress />;

  const initialValues = getEditData(editQueryResult.data);

  const onSubmit = (input: Record<string, any>) => {
    return updateItem({
      variables: {
        id,
        input,
      },
    });
  };

  const title =
    typeof texts.editItem === "function"
      ? texts.editItem(editQueryResult.data)
      : texts.editItem;

  const editActions = getEditActions && id ? getEditActions({ id }) : null;

  // Обработка динамической схемы
  const resolvedSchema =
    typeof editSchema === "function"
      ? editSchema(editQueryResult.data)
      : editSchema;

  return (
    <Dialog open={open} onClose={onClose} fullScreen fullWidth>
      <Form
        indent
        initialValues={initialValues as Record<string, any>}
        schema={resolvedSchema}
        onSubmit={onSubmit}
        onSubmitSuccess={onSubmitSuccess}
        submitButton={false}
        resetButton={false}
        editData={editQueryResult.data}
        metadata={metadata}
        paper={paper}
        header={(props: any) => (
          <InnerCrudFormHeader
            {...props}
            onClickBack={onClose}
            title={title}
            disabled={loading}
            editActions={editActions}
          />
        )}
      />
    </Dialog>
  );
}
